// Optimized Tiered Quantity Logic for Wholesale Coffee Products
// Robust implementation with multiple detection methods

class TieredQuantityManager {
  constructor() {
    // Tier rules using efficient object lookup
    this.tierRules = {
      'Tier 1 (10-25lbs)': { min: 2, max: 5, step: 1 },
      'Tier 2 (30-50lbs)': { min: 6, max: 10, step: 1 },
      'Tier 3 (55-100lbs)': { min: 11, max: 20, step: 1 },
      'Tier 4 (105-250lbs)': { min: 21, max: 50, step: 1 }
    };
    
    // Cache DOM elements to avoid repeated queries
    this.cachedElements = {};
    this.isInitialized = false;
    this.debounceTimer = null;
    
    this.init();
  }
  
  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }
  
  setup() {
    console.log('TieredQuantityManager: Starting setup');
    
    // Cache key elements once
    this.cacheElements();
    
    // Debug: Check if this is a wholesale product
    const isWholesale = this.isWholesaleProduct();
    console.log('TieredQuantityManager: Is wholesale product?', isWholesale);
    
    // Early exit for non-wholesale products
    if (!isWholesale) {
      console.log('TieredQuantityManager: Not a wholesale product, hiding quantity rules');
      this.hideQuantityRules();
      return;
    }
    
    console.log('TieredQuantityManager: Wholesale tiered product detected, setting up');
    this.setupEventListeners();
    this.applyInitialRules();
    this.isInitialized = true;
  }
  
  cacheElements() {
    const productInfo = document.querySelector('product-info');
    console.log('TieredQuantityManager: ProductInfo element found:', !!productInfo);
    
    if (!productInfo) return;
    
    const sectionId = productInfo.dataset.section;
    console.log('TieredQuantityManager: Section ID:', sectionId);
    
    this.cachedElements = {
      productInfo,
      quantityInput: document.getElementById(`Quantity-${sectionId}`),
      quantityRules: document.querySelector('.quantity__rules'),
      variantSelects: document.querySelector('variant-selects'),
      sectionId
    };
    
    console.log('TieredQuantityManager: Cached elements:', {
      hasQuantityInput: !!this.cachedElements.quantityInput,
      hasQuantityRules: !!this.cachedElements.quantityRules,
      hasVariantSelects: !!this.cachedElements.variantSelects
    });
  }
  
  isWholesaleProduct() {
    const productInfo = this.cachedElements.productInfo;
    if (!productInfo) return false;
    
    // Method 1: Check data attributes (if template has been updated)
    if (productInfo.dataset.wholesale === 'true' || productInfo.dataset.tierProduct === 'true') {
      console.log('TieredQuantityManager: Detected via data attributes');
      return true;
    }
    
    // Method 2: Check product title for "wholesale" keyword
    const productTitle = document.querySelector('.product__title h1, .product__title h2');
    if (productTitle && productTitle.textContent.toLowerCase().includes('wholesale')) {
      console.log('TieredQuantityManager: Detected via product title');
      return true;
    }
    
    // Method 3: Check if any variants contain "Tier" in their options
    const variantSelects = this.cachedElements.variantSelects;
    if (variantSelects) {
      const selects = variantSelects.querySelectorAll('select');
      for (const select of selects) {
        for (let i = 0; i < select.options.length; i++) {
          if (select.options[i].text.match(/Tier\s*\d+/i)) {
            console.log('TieredQuantityManager: Detected via variant options');
            return true;
          }
        }
      }
    }
    
    // Method 4: Check URL for wholesale indicators
    if (window.location.pathname.includes('b2b') || window.location.pathname.includes('wholesale')) {
      console.log('TieredQuantityManager: Detected via URL');
      return true;
    }
    
    console.log('TieredQuantityManager: No wholesale indicators found');
    return false;
  }
  
  hideQuantityRules() {
    if (this.cachedElements.quantityRules) {
      this.cachedElements.quantityRules.style.display = 'none';
    }
  }
  
  setupEventListeners() {
    if (!this.cachedElements.productInfo || !this.cachedElements.quantityInput) {
      console.log('TieredQuantityManager: Missing required elements for event listeners');
      return;
    }
    
    console.log('TieredQuantityManager: Setting up event listeners');
    
    // Listen for multiple Shopify variant change events
    ['variant:change', 'variant:changed'].forEach(eventType => {
      this.cachedElements.productInfo.addEventListener(eventType, 
        this.debounce((event) => this.handleVariantChange(event), 50)
      );
    });
    
    // Fallback for direct select changes
    if (this.cachedElements.variantSelects) {
      const selects = this.cachedElements.variantSelects.querySelectorAll('select');
      console.log('TieredQuantityManager: Found', selects.length, 'variant selects');
      selects.forEach(select => {
        select.addEventListener('change', 
          this.debounce((event) => this.handleSelectChange(event), 50)
        );
      });
    }
    
    // Listen for PubSub events that Shopify uses
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
        console.log('TieredQuantityManager: PubSub variant change:', event);
        if (event.data && event.data.variant) {
          setTimeout(() => {
            this.updateQuantityRules(event.data.variant.title);
          }, 100);
        }
      });
    }
    
    // Monitor add to cart button for external changes
    this.setupButtonMonitoring();
    
    // Override Shopify's quantity boundary setting
    this.overrideShopifyQuantityBoundaries();
  }
  
  overrideShopifyQuantityBoundaries() {
    // Intercept and override the setQuantityBoundries method
    const productInfo = this.cachedElements.productInfo;
    if (productInfo && typeof productInfo.setQuantityBoundries === 'function') {
      console.log('TieredQuantityManager: Overriding Shopify setQuantityBoundries method');
      const originalMethod = productInfo.setQuantityBoundries.bind(productInfo);
      productInfo.setQuantityBoundries = () => {
        // Only call original method for non-tier products or if no tier is selected
        const currentVariant = this.getCurrentVariantTitle();
        if (!this.getTierRules(currentVariant)) {
          console.log('TieredQuantityManager: Using Shopify default boundaries');
          originalMethod();
        } else {
          console.log('TieredQuantityManager: Skipping Shopify boundaries, using tier boundaries');
          // Don't call the original method for tier products to prevent conflicts
        }
      };
    }
    
    // Also override updateQuantityRules method if it exists
    if (productInfo && typeof productInfo.updateQuantityRules === 'function') {
      const originalUpdateMethod = productInfo.updateQuantityRules.bind(productInfo);
      productInfo.updateQuantityRules = (sectionId, html) => {
        const currentVariant = this.getCurrentVariantTitle();
        if (!this.getTierRules(currentVariant)) {
          // Only update if not a tier product
          originalUpdateMethod(sectionId, html);
        } else {
          console.log('TieredQuantityManager: Blocking Shopify updateQuantityRules for tier product');
        }
      };
    }
    
    // Override product form submission validation
    const productForm = document.querySelector('product-form');
    if (productForm && typeof productForm.onSubmitHandler === 'function') {
      const originalSubmitHandler = productForm.onSubmitHandler.bind(productForm);
      productForm.onSubmitHandler = (event) => {
        const currentVariant = this.getCurrentVariantTitle();
        if (this.getTierRules(currentVariant)) {
          // For tier products, ensure the form can submit even if Shopify thinks quantity is invalid
          console.log('TieredQuantityManager: Allowing tier product form submission');
          // Remove any validation that might prevent submission
          const submitButton = event.target.querySelector('[type="submit"]');
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.removeAttribute('disabled');
          }
        }
        return originalSubmitHandler(event);
      };
    }
  }
  
  debounce(func, wait) {
    return (...args) => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => func.apply(this, args), wait);
    };
  }
  
  handleVariantChange(event) {
    console.log('TieredQuantityManager: Variant change event:', event.detail);
    if (event.detail?.variant?.title) {
      this.updateQuantityRules(event.detail.variant.title);
    }
  }
  
  handleSelectChange(event) {
    const selectedText = event.target.options[event.target.selectedIndex].text;
    console.log('TieredQuantityManager: Select change detected:', selectedText);
    this.updateQuantityRules(selectedText);
  }
  
  getCurrentVariantTitle() {
    const variantSelects = this.cachedElements.variantSelects;
    if (variantSelects) {
      const select = variantSelects.querySelector('select');
      if (select && select.selectedIndex >= 0) {
        return select.options[select.selectedIndex].text;
      }
    }
    return null;
  }
  
  getTierRules(variantTitle) {
    if (!variantTitle) return null;
    
    console.log('TieredQuantityManager: Getting tier rules for:', variantTitle);
    
    // Direct lookup first (most efficient)
    if (this.tierRules[variantTitle]) {
      console.log('TieredQuantityManager: Found direct match');
      return this.tierRules[variantTitle];
    }
    
    // Fallback pattern matching
    for (const tierKey of Object.keys(this.tierRules)) {
      const tierNumber = tierKey.match(/Tier\s*(\d+)/i)?.[1];
      if (tierNumber && variantTitle.includes(`Tier ${tierNumber}`)) {
        console.log('TieredQuantityManager: Found pattern match for:', tierKey);
        return this.tierRules[tierKey];
      }
    }
    
    console.log('TieredQuantityManager: No tier rules found for:', variantTitle);
    return null;
  }
  
  updateQuantityRules(variantTitle) {
    console.log('TieredQuantityManager: Updating quantity rules for:', variantTitle);
    
    const rules = this.getTierRules(variantTitle);
    const quantityInput = this.cachedElements.quantityInput;
    
    if (!rules || !quantityInput) {
      console.log('TieredQuantityManager: No rules or no quantity input, resetting to defaults');
      this.resetToDefaults();
      return;
    }
    
    console.log('TieredQuantityManager: Applying tier rules:', rules);
    
    // Update input attributes efficiently
    this.setInputAttributes(quantityInput, rules);
    
    // Update quantity value if outside the tier range
    const currentQuantity = parseInt(quantityInput.value);
    if (currentQuantity < rules.min) {
      quantityInput.value = rules.min;
      console.log('TieredQuantityManager: Updated quantity value to minimum:', rules.min);
    } else if (rules.max !== null && currentQuantity > rules.max) {
      quantityInput.value = rules.max;
      console.log('TieredQuantityManager: Updated quantity value to maximum:', rules.max);
    }
    
    // Update display rules
    this.updateDisplayRules(rules);
    
    // Force update the add to cart button state to prevent "out of stock" issue
    this.updateAddToCartButton();
    
    // Trigger events for other scripts
    quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
  
  setInputAttributes(input, rules) {
    // Set minimum
    input.min = rules.min;
    input.setAttribute('min', rules.min);
    input.setAttribute('data-min', rules.min);
    
    // Set maximum
    if (rules.max !== null) {
      input.max = rules.max;
      input.setAttribute('max', rules.max);
      input.setAttribute('data-max', rules.max);
    } else {
      input.removeAttribute('max');
      input.removeAttribute('data-max');
      input.max = '';
    }
    
    // Set step
    input.step = rules.step;
    input.setAttribute('step', rules.step);
    
    console.log('TieredQuantityManager: Set input attributes - min:', rules.min, 'max:', rules.max, 'step:', rules.step);
  }
  
  updateDisplayRules(rules) {
    const quantityRules = this.cachedElements.quantityRules;
    if (!quantityRules) {
      console.log('TieredQuantityManager: No quantity rules element found');
      return;
    }
    
    // Create proper tier display without increment text
    const rulesHtml = [
      `<span class="divider">Minimum of ${rules.min}</span>`,
      rules.max !== null 
        ? `<span class="divider">Maximum of ${rules.max}</span>`
        : '<span class="divider">No maximum limit</span>'
    ].join('');
    
    quantityRules.innerHTML = rulesHtml;
    quantityRules.style.display = 'block';
    
    console.log('TieredQuantityManager: Updated display rules');
  }
  
  updateAddToCartButton() {
    // Find and update the add to cart button to ensure it's not disabled
    const addToCartButton = document.querySelector('product-form [type="submit"]');
    const currentVariant = this.getCurrentVariantObject();
    
    console.log('TieredQuantityManager: Checking add to cart button state');
    console.log('TieredQuantityManager: Button found:', !!addToCartButton);
    console.log('TieredQuantityManager: Current variant:', currentVariant);
    
    if (addToCartButton) {
      if (currentVariant && currentVariant.available) {
        addToCartButton.disabled = false;
        addToCartButton.removeAttribute('disabled');
        const buttonText = addToCartButton.querySelector('span');
        if (buttonText) {
          buttonText.textContent = 'Add to cart';
        }
        console.log('TieredQuantityManager: Enabled add to cart button for available variant');
      } else {
        // For tier products, we should keep the button enabled even if Shopify thinks it's not available
        // This is because tier quantities might be different from Shopify's default inventory check
        const currentTier = this.getCurrentVariantTitle();
        if (this.getTierRules(currentTier)) {
          addToCartButton.disabled = false;
          addToCartButton.removeAttribute('disabled');
          const buttonText = addToCartButton.querySelector('span');
          if (buttonText) {
            buttonText.textContent = 'Add to cart';
          }
          console.log('TieredQuantityManager: Force enabled add to cart button for tier product');
        } else {
          addToCartButton.disabled = true;
          addToCartButton.setAttribute('disabled', 'disabled');
          const buttonText = addToCartButton.querySelector('span');
          if (buttonText) {
            buttonText.textContent = 'Sold out';
          }
          console.log('TieredQuantityManager: Disabled add to cart button - variant not available');
        }
      }
    }
  }
  
  getCurrentVariantObject() {
    // Try to get the current variant object from various sources
    const variantInput = document.querySelector('.product-variant-id');
    if (variantInput && variantInput.value) {
      // Get variant data from the JSON script tag
      const variantScript = document.querySelector('variant-selects script[type="application/json"]');
      if (variantScript) {
        try {
          const variants = JSON.parse(variantScript.textContent);
          return variants.find(v => v.id == variantInput.value);
        } catch (e) {
          console.log('TieredQuantityManager: Could not parse variant JSON');
        }
      }
    }
    return null;
  }
  
  resetToDefaults() {
    const quantityInput = this.cachedElements.quantityInput;
    if (!quantityInput) return;
    
    console.log('TieredQuantityManager: Resetting to defaults');
    
    // Reset to Shopify defaults
    quantityInput.min = 1;
    quantityInput.setAttribute('min', '1');
    quantityInput.setAttribute('data-min', '1');
    quantityInput.removeAttribute('max');
    quantityInput.removeAttribute('data-max');
    quantityInput.max = '';
    quantityInput.step = 1;
    quantityInput.setAttribute('step', '1');
    
    if (this.cachedElements.quantityRules) {
      this.cachedElements.quantityRules.innerHTML = '<span class="divider">Minimum of 1</span>';
    }
    
    // Update add to cart button for non-tier variants
    this.updateAddToCartButton();
  }
  
  setupButtonMonitoring() {
    // Monitor the add to cart button for external changes that might disable it
    const addToCartButton = document.querySelector('product-form [type="submit"]');
    if (!addToCartButton) return;
    
    console.log('TieredQuantityManager: Setting up button monitoring');
    
    // Use MutationObserver to watch for disabled attribute changes
    const buttonObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
          const currentTier = this.getCurrentVariantTitle();
          if (this.getTierRules(currentTier)) {
            console.log('TieredQuantityManager: Button was externally disabled, re-enabling for tier product');
            setTimeout(() => this.updateAddToCartButton(), 10);
          }
        }
      });
    });
    
    buttonObserver.observe(addToCartButton, {
      attributes: true,
      attributeFilter: ['disabled']
    });
    
    // Also check periodically
    setInterval(() => {
      const currentTier = this.getCurrentVariantTitle();
      if (this.getTierRules(currentTier) && addToCartButton.disabled) {
        console.log('TieredQuantityManager: Periodic check - re-enabling button for tier product');
        this.updateAddToCartButton();
      }
    }, 1000);
  }
  
  applyInitialRules() {
    // Apply rules for initially selected variant
    console.log('TieredQuantityManager: Applying initial rules');
    setTimeout(() => {
      const currentVariant = this.getCurrentVariantTitle();
      console.log('TieredQuantityManager: Initial variant:', currentVariant);
      if (currentVariant) {
        this.updateQuantityRules(currentVariant);
      }
    }, 100);
  }
}

// Initialize once DOM is ready
console.log('TieredQuantityManager: Script loaded');
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('TieredQuantityManager: DOM loaded, initializing');
    window.tieredQuantityManager = new TieredQuantityManager();
  });
} else {
  console.log('TieredQuantityManager: DOM already loaded, initializing immediately');
  window.tieredQuantityManager = new TieredQuantityManager();
}